import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { type MouseEvent, useEffect, useRef, useState } from "react";
import { useMarkEmailVerified, useResendInvitation, useUserClerkAccount } from "@/hooks/useUsers";
import type { User, UserActionToast } from "@/interfaces/user";
import { apiErrorMessage } from "@/utils/apiError-helper";
import {
  type RowAccountState,
  type UserRowActionItem,
  type UserRowActionKey,
  type UserRowActionPerms,
  userRowActions,
} from "@/utils/userRowActions-helper";

interface UserRowMenuProps {
  user: User;
  perms: UserRowActionPerms;
  onToast: (toast: UserActionToast) => void;
  onChangeEmail: (user: User) => void;
}

const ITEM_CLASS =
  "!items-start !rounded-[6px] !px-2 !py-[7px] !font-lato !text-sm !font-medium !text-graphite hover:!bg-light-dust";

export function UserRowMenu({ user, perms, onToast, onChangeEmail }: UserRowMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const account = useUserClerkAccount(user.id, open && Boolean(user.clerkUserId));
  const resend = useResendInvitation();
  const markVerified = useMarkEmailVerified();

  // The menu is anchored to a button that moves with the table; close on scroll/resize rather
  // than let it float detached. Capture phase catches the table's own scroll container.
  useEffect(() => {
    if (!open) return;
    const close = () => setAnchorEl(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const accountState: RowAccountState = (() => {
    if (!open || !user.clerkUserId) return { status: "idle" };
    if (account.isError) return { status: "error" };
    if (account.data) return { status: "ready", value: account.data };
    return { status: "loading" };
  })();

  const items = userRowActions(user, perms, accountState);

  const emptyLabel = (() => {
    if (!user.clerkUserId) return "No actions available";
    if (accountState.status === "error") return "Couldn't load account details";
    if (accountState.status === "ready") return "No actions available";
    return "Checking account…";
  })();

  function openMenu(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function closeMenu() {
    setAnchorEl(null);
  }

  async function runResend() {
    try {
      await resend.mutateAsync(user.id);
      onToast({ severity: "success", message: `Invitation resent to ${user.email}` });
    } catch (err) {
      onToast({
        severity: "error",
        message: apiErrorMessage(err, "Resend invitation failed. Try again."),
      });
    }
  }

  async function runMarkVerified() {
    try {
      const result = await markVerified.mutateAsync(user.id);
      onToast({
        severity: "success",
        message: result.alreadyVerified ? "Email was already verified" : "Email marked as verified",
      });
    } catch (err) {
      onToast({
        severity: "error",
        message: apiErrorMessage(err, "Mark email as verified failed. Try again."),
      });
    }
  }

  // Change Email opens a dialog; running it while the Menu is still fading out would have the
  // Menu's focus restore steal the dialog's focus, so it waits for the exit transition.
  const pendingAction = useRef<(() => void) | null>(null);

  function handleExited() {
    const action = pendingAction.current;
    pendingAction.current = null;
    action?.();
  }

  function handleItem(key: UserRowActionKey) {
    if (key === "changeEmail") pendingAction.current = () => onChangeEmail(user);
    closeMenu();
    if (key === "resendInvite") void runResend();
    else if (key === "markEmailVerified") void runMarkVerified();
  }

  function renderItem(item: UserRowActionItem) {
    return (
      <MenuItem
        key={item.key}
        disabled={item.disabled}
        className={ITEM_CLASS}
        onClick={() => handleItem(item.key)}
      >
        <span className="flex flex-col">
          <span>{item.label}</span>
          {item.disabled && item.reason ? (
            <span className="text-xs font-normal text-graphite">{item.reason}</span>
          ) : null}
        </span>
      </MenuItem>
    );
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={openMenu}
        aria-label={`More actions for ${user.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="!text-graphite"
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        slotProps={{
          transition: { onExited: handleExited },
          paper: {
            className: "min-w-[200px] rounded-[8px] border border-smoke bg-white p-[6px] shadow-lg",
          },
        }}
      >
        {items.length === 0 ? (
          <MenuItem disabled className={ITEM_CLASS}>
            {emptyLabel}
          </MenuItem>
        ) : (
          items.map(renderItem)
        )}
      </Menu>
    </>
  );
}

export default UserRowMenu;
